import { useAppDispatch, useAppSelector } from 'app/config/store';
import SelectPaginate from 'app/shared/components/select-paginate/SelectPaginate';
import { TireStatus } from 'app/shared/model/enumerations/tire-status.model';
import { ITireTruckImplement } from 'app/shared/model/tire-truck-implement.model';
import React, { useEffect, useState } from 'react';
import { translate } from 'react-jhipster';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import TireDetail from '../component/tire-detail';
import TireVehicleOdometerField from '../component/tire-vehicle-odometer-field';
import { renderStatus } from '../component/tire-status';
import { saveAllTireTireTruckImplements } from './tire-truck-implement-custom.reducer';
import { TireDiscardReason } from 'app/shared/model/enumerations/tire-discard-reason.model';
import { BoxForm } from 'app/shared/layout/register/DefaultFormsComponents';
import { RegisterButton } from 'app/shared/layout/register/RegisterButton';
import { isVehicleModelTireFunctionComplete } from '../util/tire-function-completeness';

export interface ITirePositions {
  position: number;
  steppe: boolean;
  selected: boolean;
}

const TireTruckImplementCustomRemoveDialog = () => {
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedTire, setSelectedTire] = useState<ITireTruckImplement | null>(null);
  const [selectedTireStatus, setSelectedTireStatus] = useState<TireStatus | null>(null);
  const [discardReason, setDiscardReason] = useState(null);
  const [vehicleOdometer, setVehicleOdometer] = useState('');

  const updateSuccess = useAppSelector(state => state.tireTruckImplementCustom.updateSuccess);
  const tireTruckImplements = useAppSelector(state => state.tireTruckImplementCustom.entities);
  const truckImplementTireDataDTO = useAppSelector(state => state.tireTruckImplementCustom.truckImplementTireDataDTO);
  const truckImplementId = tireTruckImplements[0]?.truckImplement?.id;

  const tireFunctionComplete = isVehicleModelTireFunctionComplete(
    truckImplementTireDataDTO?.vehicleModel,
    truckImplementTireDataDTO?.numberOfTires
  );

  const toggle = () => {
    setIsOpen(!isOpen);
    clearAll();
  };

  useEffect(() => {
    if (isOpen && updateSuccess) {
      toggle();
    }
  }, [updateSuccess]);

  const handleTireStatusChange = (event: { target: { value: string } }) => {
    const status = event.target.value as TireStatus;

    setSelectedTireStatus(status);

    if (status !== 'WASTE') {
      setDiscardReason(null);
    }
  };

  const clearAll = () => {
    setSelectedTire(null);
    setSelectedTireStatus(null);
    setDiscardReason(null);
    setVehicleOdometer('');
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!tireFunctionComplete) {
      return toast.error(translate('fleetSenseApp.tireTruckImplement.incompleteVehicleModelError'));
    }

    if (!selectedTire) {
      return toast.error('Selecione o pneu');
    }

    if (!selectedTireStatus) {
      return toast.error('Selecione o destino do pneu');
    }

    if (!vehicleOdometer) {
      return toast.error('Insira o odômetro do veículo');
    }

    if (selectedTireStatus === 'WASTE' && !discardReason) {
      return toast.error('Selecione o motivo do descarte');
    }

    const installOdometer = Number(vehicleOdometer);

    const tireTruckImplement: ITireTruckImplement = {
      id: selectedTire.id,
      position: selectedTire.position,
      steppe: selectedTire.steppe,
      tire: {
        ...selectedTire.tire,
        status: selectedTireStatus,
        discardReason: selectedTireStatus === 'WASTE' ? discardReason : null,
      },
      truckImplement: {
        id: truckImplementId,
      },
      installOdometer,
    };

    dispatch(saveAllTireTireTruckImplements([tireTruckImplement]));
  };

  const sortedTireTruckImplements = [...tireTruckImplements].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const tireStatusOptions = [
    { id: 'STOCK', label: translate('fleetSenseApp.TireStatus.STOCK') },
    { id: 'RECAP', label: translate('fleetSenseApp.TireStatus.RECAP') },
    { id: 'WASTE', label: translate('fleetSenseApp.TireStatus.WASTE') },
  ];

  const discardReasonOptions = Object.keys(TireDiscardReason).map(key => ({
    id: key,
    label: translate(`fleetSenseApp.TireDiscardReason.${key}`),
  }));

  return (
    <>
      <Button variant="contained" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => toggle()}>
        Remover
      </Button>

      {isOpen && (
        <Dialog
          open={isOpen}
          onClose={toggle}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 1,
              bgcolor: 'primary.main',
              backgroundImage: 'none',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: 1.5,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Remover pneu
            </Typography>
            <IconButton onClick={toggle} size="small" aria-label="Fechar">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <Divider />

          <BoxForm onSubmit={handleSubmit}>
          <DialogContent sx={{ py: 2.5 }}>
            <Stack spacing={2}>
              {!tireFunctionComplete && (
                <Alert severity="warning">
                  {translate('fleetSenseApp.tireTruckImplement.incompleteVehicleModelWarning')}
                </Alert>
              )}
              <TireVehicleOdometerField truckImplementId={truckImplementId ?? ''} value={vehicleOdometer} onChange={setVehicleOdometer} />

              <div>
                <Typography component="div" gutterBottom>
                  Selecione o pneu {renderStatus(TireStatus.INSTALLED)}
                </Typography>
                <SelectPaginate
                  value={selectedTire}
                  staticOptions={sortedTireTruckImplements}
                  onChange={option => setSelectedTire(option ?? null)}
                  displayFormat={option => `${option?.tire?.marking} - ${option?.position}`}
                  menuPortalTarget={document.body}
                />
                {selectedTire && (
                  <TireDetail
                    tire={{ ...selectedTire?.tire, totalRecaps: undefined }}
                    position={selectedTire?.position ?? undefined}
                    steppe={selectedTire?.steppe ?? undefined}
                  />
                )}
              </div>

              {selectedTire && (
                <div>
                  <Typography component="div" gutterBottom>
                    Destino
                  </Typography>
                  <SelectPaginate
                    value={selectedTireStatus ? tireStatusOptions.find(o => o.id === selectedTireStatus) ?? null : null}
                    staticOptions={tireStatusOptions}
                    onChange={option => handleTireStatusChange({ target: { value: option?.id ?? '' } })}
                    displayFormat={option => option?.label}
                    menuPortalTarget={document.body}
                  />
                </div>
              )}

              {selectedTireStatus === 'WASTE' && (
                <div>
                  <Typography component="div" gutterBottom>
                    {translate('fleetSenseApp.tire.discardReason')}
                  </Typography>
                  <SelectPaginate
                    value={discardReason ? discardReasonOptions.find(o => o.id === discardReason) ?? null : null}
                    staticOptions={discardReasonOptions}
                    onChange={option => setDiscardReason(option?.id ?? null)}
                    displayFormat={option => option?.label}
                    menuPortalTarget={document.body}
                  />
                </div>
              )}
            </Stack>
          </DialogContent>

          <Divider />

          <Box sx={{ px: 3, py: 2 }}>
            <RegisterButton onBack={() => setIsOpen(false)} />
          </Box>
          </BoxForm>
        </Dialog>
      )}
    </>
  );
};

export default TireTruckImplementCustomRemoveDialog;
